import React from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  NativeSelect,
  Stack,
  Text,
  VStack,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { FaFolder, FaGripVertical, FaListOl, FaPlus, FaTrash } from 'react-icons/fa';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableProgramaRow({ id, index, titulo, subtitulo, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const border = useColorModeValue('gray.200', 'gray.600');
  const bg = useColorModeValue('white', 'gray.800');
  const dragBg = useColorModeValue('purple.50', 'rgba(88, 28, 135, 0.35)');

  return (
    <Flex
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : undefined,
      }}
      align="center"
      gap={2}
      py={2.5}
      px={3}
      borderWidth="1px"
      rounded="xl"
      borderColor={isDragging ? 'purple.400' : border}
      bg={isDragging ? dragBg : bg}
      shadow={isDragging ? 'md' : 'sm'}
    >
      <Flex
        align="center"
        justify="center"
        w={7}
        h={7}
        rounded="md"
        bg="purple.100"
        color="purple.700"
        fontWeight="800"
        fontSize="xs"
        flexShrink={0}
        _dark={{ bg: 'purple.900', color: 'purple.200' }}
      >
        {index}
      </Flex>
      <IconButton
        size="sm"
        variant="ghost"
        aria-label="Arrastrar para reordenar"
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        {...attributes}
        {...listeners}><FaGripVertical /></IconButton>
      <Box minW={0} flex={1}>
        <Text fontSize="sm" fontWeight="600" lineClamp={2}>
          {titulo}
        </Text>
        {subtitulo ? (
          <Text fontSize="xs" color="gray.500" lineClamp={1} mt={0.5}>
            {subtitulo}
          </Text>
        ) : null}
      </Box>
      <IconButton
        size="sm"
        variant="ghost"
        colorPalette="red"
        aria-label="Quitar de la carpeta"
        onClick={() => onRemove(id)}><FaTrash /></IconButton>
    </Flex>
  );
}

export default function BandaRepertorioCarpetaObrasModal({
  isOpen,
  carpeta,
  onClose,
  obras,
  generosCatalog,
  ordenIds,
  onToggleObra,
  onDragEnd,
  search,
  onSearchChange,
  generoId,
  onGeneroIdChange,
  estado,
  onEstadoChange,
  filteredObras,
  onSave,
  saving,
}) {
  const panelBg = useColorModeValue('gray.50', 'gray.900');
  const panelBorder = useColorModeValue('gray.200', 'gray.600');
  const accentPanelBg = useColorModeValue('purple.50', 'rgba(88, 28, 135, 0.2)');
  const cardHover = useColorModeValue('purple.50', 'rgba(88, 28, 135, 0.25)');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const obrasEnPrograma = ordenIds
    .map((oid) => obras.find((x) => x.id === oid))
    .filter(Boolean);

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" placement='center' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <Dialog.Positioner>
          <Dialog.Content
            maxW={{ base: '100%', lg: '1050px' }}
            mx={3}
            rounded="2xl"
            overflow="hidden">
            <Dialog.Header borderBottomWidth="1px" pb={4}>
              <HStack align="flex-start" gap={3} pr={8}>
                <Flex
                  align="center"
                  justify="center"
                  w={11}
                  h={11}
                  rounded="xl"
                  bg="purple.100"
                  color="purple.600"
                  flexShrink={0}
                  _dark={{ bg: 'purple.900', color: 'purple.200' }}
                >
                  <Icon boxSize={5} asChild><FaFolder /></Icon>
                </Flex>
                <Box flex={1} minW={0}>
                  <Text fontSize="xs" fontWeight="700" color="purple.600" textTransform="uppercase" letterSpacing="wide">
                    Gestionar obras
                  </Text>
                  <Heading size="md" mt={0.5} lineClamp={2}>
                    {carpeta?.nombre || 'Carpeta'}
                  </Heading>
                  {carpeta?.descripcion ? (
                    <Text fontSize="sm" color="gray.500" mt={1} lineClamp={2}>
                      {carpeta.descripcion}
                    </Text>
                  ) : null}
                </Box>
                <Badge colorPalette="purple" fontSize="0.8rem" px={3} py={1} rounded="full" alignSelf="center">
                  {ordenIds.length} en carpeta
                </Badge>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger top={4} right={4} />

            <Dialog.Body py={5}>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Marca obras en la columna izquierda para añadirlas. En la derecha define el orden de interpretación
                arrastrando las filas.
              </Text>

              <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5} alignItems="stretch">
                <Box
                  borderWidth="1px"
                  borderColor={panelBorder}
                  rounded="2xl"
                  overflow="hidden"
                  bg={panelBg}
                  display="flex"
                  flexDirection="column"
                  minH={{ base: 'auto', lg: '420px' }}
                >
                  <HStack px={4} py={3} borderBottomWidth="1px" borderColor={panelBorder} bg="white" _dark={{ bg: 'gray.800' }}>
                    <Icon color="purple.500" asChild><FaPlus /></Icon>
                    <Text fontWeight="700" fontSize="sm">
                      Añadir obras del repertorio
                    </Text>
                  </HStack>

                  <Box px={4} py={3} borderBottomWidth="1px" borderColor={panelBorder}>
                    <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={2} mb={2}>
                      <Field.Root gridColumn={{ base: '1 / -1', sm: '1 / -1' }}>
                        <Field.Label fontSize="xs" mb={1}>
                          Buscar
                        </Field.Label>
                        <Input
                          size="sm"
                          placeholder="Título, autor, notas…"
                          value={search}
                          onChange={(e) => onSearchChange(e.target.value)}
                          bg="white"
                          _dark={{ bg: 'gray.800' }}
                        />
                      </Field.Root>
                      <Field.Root>
                        <Field.Label fontSize="xs" mb={1}>
                          Género
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="sm"
                            value={generoId}
                            onChange={(e) => onGeneroIdChange(e.target.value)}
                            bg="white"
                            _dark={{ bg: 'gray.800' }}>
                            <option value="">Todos</option>
                            {generosCatalog.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.nombre}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root>
                        <Field.Label fontSize="xs" mb={1}>
                          Estado
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="sm"
                            value={estado}
                            onChange={(e) => onEstadoChange(e.target.value)}
                            bg="white"
                            _dark={{ bg: 'gray.800' }}>
                            <option value="">Todos</option>
                            <option value="BORRADOR">Borrador</option>
                            <option value="PROCESANDO">Procesando</option>
                            <option value="LISTO">Listo</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    </Grid>
                    <Text fontSize="xs" color="gray.500">
                      {filteredObras.length} obra{filteredObras.length === 1 ? '' : 's'} con los filtros actuales
                    </Text>
                    <HStack gap={2} mt={2} flexWrap="wrap">
                      <Button
                        size="xs"
                        variant="outline"
                        colorPalette="purple"
                        disabled={!filteredObras.length}
                        onClick={() => {
                          filteredObras.forEach((o) => {
                            if (!ordenIds.includes(o.id)) onToggleObra(o.id, true);
                          });
                        }}
                      >
                        Seleccionar todas (filtro)
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={!filteredObras.length}
                        onClick={() => {
                          filteredObras.forEach((o) => {
                            if (ordenIds.includes(o.id)) onToggleObra(o.id, false);
                          });
                        }}
                      >
                        Quitar todas (filtro)
                      </Button>
                    </HStack>
                  </Box>

                  <Stack gap={2} px={3} py={3} flex={1} overflowY="auto" maxH={{ base: '280px', lg: 'none' }}>
                    {filteredObras.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" px={1}>
                        Ninguna obra coincide. Prueba a ampliar la búsqueda o los filtros.
                      </Text>
                    ) : (
                      filteredObras.map((o) => {
                        const checked = ordenIds.includes(o.id);
                        return (
                          <Flex
                            key={o.id}
                            as="label"
                            align="flex-start"
                            gap={3}
                            p={3}
                            rounded="xl"
                            borderWidth="1px"
                            borderColor={checked ? 'purple.300' : panelBorder}
                            bg={checked ? accentPanelBg : 'white'}
                            _dark={{ bg: checked ? 'rgba(88, 28, 135, 0.35)' : 'gray.800' }}
                            cursor="pointer"
                            transition="all 0.15s ease"
                            _hover={{ borderColor: 'purple.300', bg: cardHover }}
                          >
                            <Checkbox.Root
                              onCheckedChange={(details) =>
                                onToggleObra(o.id, details.checked === true)
                              }
                              colorPalette="purple"
                              mt={0.5}
                              checked={checked}
                            >
                              <Checkbox.HiddenInput />
                              <Checkbox.Control>
                                <Checkbox.Indicator />
                              </Checkbox.Control>
                            </Checkbox.Root>
                            <Box minW={0} flex={1}>
                              <Text fontWeight="600" fontSize="sm" lineClamp={2}>
                                {o.titulo}
                              </Text>
                              {o.autor ? (
                                <Text fontSize="xs" color="gray.500" lineClamp={1} mt={0.5}>
                                  {o.autor}
                                </Text>
                              ) : null}
                              <HStack gap={1} mt={1.5} flexWrap="wrap">
                                <Badge fontSize="0.6rem">{o.estado}</Badge>
                                {o.genero?.nombre ? (
                                  <Badge fontSize="0.6rem" variant="outline" colorPalette="purple">
                                    {o.genero.nombre}
                                  </Badge>
                                ) : null}
                              </HStack>
                            </Box>
                          </Flex>
                        );
                      })
                    )}
                  </Stack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderColor={panelBorder}
                  rounded="2xl"
                  overflow="hidden"
                  bg={accentPanelBg}
                  display="flex"
                  flexDirection="column"
                  minH={{ base: 'auto', lg: '420px' }}
                >
                  <HStack px={4} py={3} borderBottomWidth="1px" borderColor={panelBorder} bg="white" _dark={{ bg: 'gray.800' }}>
                    <Icon color="purple.500" asChild><FaListOl /></Icon>
                    <Text fontWeight="700" fontSize="sm">
                      Orden del programa
                    </Text>
                  </HStack>

                  <Box px={4} py={3} flex={1} overflowY="auto" maxH={{ base: '320px', lg: 'none' }}>
                    <Text fontSize="xs" color="gray.600" mb={3}>
                      Arrastra para reordenar. Quitar una obra solo la saca de la carpeta, no la borra del repertorio.
                    </Text>

                    {ordenIds.length === 0 ? (
                      <VStack py={10} gap={2} color="gray.500">
                        <Icon boxSize={8} opacity={0.35} asChild><FaListOl /></Icon>
                        <Text fontSize="sm" textAlign="center" px={4}>
                          Aún no hay obras en esta carpeta. Márcalas en la columna de la izquierda.
                        </Text>
                      </VStack>
                    ) : (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={ordenIds} strategy={verticalListSortingStrategy}>
                          <Stack gap={2}>
                            {obrasEnPrograma.map((o, idx) => {
                              const sub = [o.autor, o.genero?.nombre].filter(Boolean).join(' · ');
                              return (
                                <SortableProgramaRow
                                  key={o.id}
                                  id={o.id}
                                  index={idx + 1}
                                  titulo={o.titulo}
                                  subtitulo={sub}
                                  onRemove={(id) => onToggleObra(id, false)}
                                />
                              );
                            })}
                          </Stack>
                        </SortableContext>
                      </DndContext>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Dialog.Body>

            <Dialog.Footer borderTopWidth="1px" gap={2} flexWrap="wrap">
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button colorPalette="purple" onClick={onSave} loading={saving}>
                Guardar cambios
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
